interface RiskInstance {
  publishRiskData: () => Promise<string>;
}

interface WindowWithRisk extends Window {
  Risk?: {
    create: (publicKey: string) => Promise<RiskInstance>;
  };
}

class RiskService {
  private publicKey: string;
  private riskInstance: RiskInstance | null;
  private initializing: Promise<RiskInstance | null> | null;

  constructor(publicKey: string) {
    this.publicKey = publicKey;
    this.riskInstance = null;
    this.initializing = null;
  }

  async init(): Promise<RiskInstance | null> {
    try {
      if (this.riskInstance) {
        return this.riskInstance;
      }

      if (this.initializing) {
        return this.initializing;
      }

      if (typeof window === "undefined" || !(window as WindowWithRisk).Risk) {
        return null;
      }

      this.initializing = (window as WindowWithRisk)
        .Risk!.create(this.publicKey)
        .then((risk: RiskInstance) => {
          this.riskInstance = risk;
          return risk;
        })
        .catch(() => null)
        .finally(() => {
          this.initializing = null;
        });

      return this.initializing;
    } catch {
      this.initializing = null;
      return null;
    }
  }

  async getSessionId(): Promise<string | null> {
    try {
      const risk = await this.init();

      if (!risk || typeof risk.publishRiskData !== "function") {
        return null;
      }

      const sessionId = await risk.publishRiskData();
      return typeof sessionId === "string" ? sessionId : null;
    } catch {
      return null;
    }
  }
}

export default RiskService;
